// hooks/useImagePreloader.js
import { useState, useEffect, useCallback } from 'react';
import { preloadImages } from '../utilities';

/**
 * Hook personalizado para precargar imágenes de forma optimizada
 * @param {Array} images - Array de URLs de imágenes a precargar
 * @param {Object} options - Opciones de configuración
 * @param {number} options.batchSize - Tamaño del lote para precarga (default: 5)
 * @param {number} options.batchDelay - Delay entre lotes en ms (default: 100)
 * @param {Array} options.priorityImages - Imágenes con prioridad alta
 * @returns {Object} - Estado de precarga y funciones de control
 */
export const useImagePreloader = (images = [], options = {}) => {
  const {
    batchSize = 5,
    batchDelay = 100,
    priorityImages = []
  } = options;

  const [loadedImages, setLoadedImages] = useState(new Set());
  const [loadingImages, setLoadingImages] = useState(new Set());
  const [failedImages, setFailedImages] = useState(new Set());
  const [isPreloading, setIsPreloading] = useState(false);
  const [progress, setProgress] = useState(0);

  const updateProgress = useCallback((loaded, total) => {
    setProgress(total > 0 ? Math.round((loaded / total) * 100) : 0);
  }, []);

  const preloadImage = useCallback(async (src) => {
    if (loadedImages.has(src) || failedImages.has(src)) {
      return Promise.resolve();
    }

    setLoadingImages(prev => new Set([...prev, src]));

    try {
      await preloadImages([src]);
      setLoadedImages(prev => {
        const newSet = new Set([...prev, src]);
        updateProgress(newSet.size, images.length);
        return newSet;
      });
      setLoadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(src);
        return newSet;
      });
    } catch (error) {
      console.warn(`Failed to preload image: ${src}`);
      setFailedImages(prev => new Set([...prev, src]));
      setLoadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(src);
        return newSet;
      });
    }
  }, [loadedImages, failedImages, images.length, updateProgress]);

  const preloadInBatches = useCallback(async (imagesToLoad) => {
    setIsPreloading(true);

    try {
      // Primero precargar imágenes prioritarias
      if (priorityImages.length > 0) {
        const validPriorityImages = priorityImages.filter(img => imagesToLoad.includes(img));
        await Promise.allSettled(
          validPriorityImages.map(src => preloadImage(src))
        );
      }

      // Luego precargar el resto en lotes
      const remainingImages = imagesToLoad.filter(img => !priorityImages.includes(img));

      for (let i = 0; i < remainingImages.length; i += batchSize) {
        const batch = remainingImages.slice(i, i + batchSize);
        await Promise.allSettled(
          batch.map(src => preloadImage(src))
        );

        // Pausa entre lotes para no sobrecargar
        if (i + batchSize < remainingImages.length) {
          await new Promise(resolve => setTimeout(resolve, batchDelay));
        }
      }
    } catch (error) {
      console.error('Error during batch preloading:', error);
    } finally {
      setIsPreloading(false);
    }
  }, [priorityImages, batchSize, batchDelay, preloadImage]);

  // Efecto principal para iniciar la precarga
  useEffect(() => {
    if (images.length > 0) {
      preloadInBatches(images);
    }
  }, [images, preloadInBatches]);

  // Función para reiniciar la precarga
  const restart = useCallback(() => {
    setLoadedImages(new Set());
    setLoadingImages(new Set());
    setFailedImages(new Set());
    setProgress(0);
    if (images.length > 0) {
      preloadInBatches(images);
    }
  }, [images, preloadInBatches]);

  // Función para verificar si una imagen está cargada
  const isImageLoaded = useCallback((src) => {
    return loadedImages.has(src);
  }, [loadedImages]);

  // Función para verificar si una imagen está cargando
  const isImageLoading = useCallback((src) => {
    return loadingImages.has(src);
  }, [loadingImages]);

  // Función para verificar si una imagen falló
  const isImageFailed = useCallback((src) => {
    return failedImages.has(src);
  }, [failedImages]);

  return {
    loadedImages,
    loadingImages,
    failedImages,
    isPreloading,
    progress,
    isImageLoaded,
    isImageLoading,
    isImageFailed,
    restart,
    totalImages: images.length,
    loadedCount: loadedImages.size,
    failedCount: failedImages.size
  };
};

export default useImagePreloader;